/**
 * Chase List Parser Service
 *
 * Handles:
 * 1. Reading Excel/CSV files into raw cell data
 * 2. Sending raw data to Gemini for AI-assisted parsing
 * 3. Returning structured chase list data for human review
 *
 * Separate from the chatbot Gemini integration — uses its own prompt and service.
 */

import { API_CONFIG, AI_CONFIG } from '../config';
import { createChaseListParsingPrompt, ParsedChaseList, OrgLookupEntry, ExistingCampaignForMerge } from '../prompts/chaseListParsingPrompt';
import { useChatbotStore } from '../store/chatbot.store';
import * as XLSX from 'xlsx';

export interface WorkbookReadResult {
  /** Visible sheets only — safe to enumerate/parse. Hidden and very-hidden sheets are excluded. */
  sheetNames: string[];
  /** Names of sheets excluded because Excel marked them Hidden (1) or VeryHidden (2). Callers may surface as an info line. */
  hiddenSheetNames: string[];
  workbook: XLSX.WorkBook;
}

/**
 * Split a workbook's sheet list into visible vs hidden according to the
 * `Workbook.Sheets[i].Hidden` visibility flag (0 = visible, 1 = hidden,
 * 2 = very hidden). Workbooks with no `Workbook` metadata (common for
 * single-sheet exports and CSVs parsed as xlsx) treat every sheet as
 * visible. Order of `SheetNames` is preserved in both output lists.
 */
const splitVisibleSheets = (workbook: XLSX.WorkBook): { visible: string[]; hidden: string[] } => {
  const meta = workbook.Workbook?.Sheets;
  if (!meta || meta.length === 0) return { visible: [...workbook.SheetNames], hidden: [] };
  const visible: string[] = [];
  const hidden: string[] = [];
  workbook.SheetNames.forEach((name, i) => {
    const state = meta[i]?.Hidden;
    if (state === 1 || state === 2) hidden.push(name);
    else visible.push(name);
  });
  return { visible, hidden };
};

/**
 * Read an Excel file. Returns VISIBLE sheet names only in `sheetNames`;
 * hidden and very-hidden sheets are surfaced separately in `hiddenSheetNames`
 * so callers can display a "N hidden sheets skipped" info line if useful.
 */
export const readExcelFile = async (file: File): Promise<WorkbookReadResult> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array', cellDates: true, cellStyles: true });
        const { visible, hidden } = splitVisibleSheets(workbook);
        resolve({ sheetNames: visible, hiddenSheetNames: hidden, workbook });
      } catch (err) {
        reject(new Error(`Failed to read Excel file: ${err}`));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Read a CSV file. CSVs have no concept of hidden sheets, so
 * `hiddenSheetNames` is always empty — the field exists for API parity
 * with `readExcelFile`.
 */
export const readCSVFile = async (file: File): Promise<WorkbookReadResult> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target?.result as string;
        const workbook = XLSX.read(text, { type: 'string', cellDates: true, cellStyles: true });
        resolve({ sheetNames: workbook.SheetNames, hiddenSheetNames: [], workbook });
      } catch (err) {
        reject(new Error(`Failed to read CSV file: ${err}`));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsText(file);
  });
};

/**
 * Extract raw cell data from a specific sheet as a JSON string
 * suitable for sending to Gemini
 */
export interface SheetExtractionResult {
  data: string;
  hiddenRowCount: number;
  totalRows: number;
  visibleRows: number;
}

export const extractSheetData = (
  workbook: XLSX.WorkBook,
  sheetName: string
): SheetExtractionResult => {
  const sheet = workbook.Sheets[sheetName];
  if (!sheet) throw new Error(`Sheet "${sheetName}" not found`);

  // Detect hidden rows. Excel exposes "hidden" three ways:
  //   1. Explicit `hidden` attribute — set by Excel's Hide command.
  //   2. Row height 0 — some workbooks use `ht="0"`.
  //   3. Row height near-zero — Checkers weekly chase lists (among
  //      others) hide old weeks by squashing the row height to a
  //      fraction of a point (e.g. `ht="0.75"` or `ht="0.47..."`).
  //      Technically nonzero but visually invisible.
  // Threshold of 5 (points or pixels) sits comfortably between the
  // real minimum usable row height (~12pt) and the "squashed to
  // invisible" values (all under 1pt in the samples we've seen).
  // `hpx` = pixel height, `hpt` = point height — check both since
  // SheetJS may populate either depending on how the file was written.
  const HIDDEN_HEIGHT_THRESHOLD = 5;
  const hiddenRows = new Set<number>();
  if (sheet['!rows']) {
    sheet['!rows'].forEach((rowInfo: any, idx: number) => {
      if (!rowInfo) return;
      if (rowInfo.hidden) {
        hiddenRows.add(idx);
        return;
      }
      const hpx = typeof rowInfo.hpx === 'number' ? rowInfo.hpx : null;
      const hpt = typeof rowInfo.hpt === 'number' ? rowInfo.hpt : null;
      if (
        (hpx !== null && hpx < HIDDEN_HEIGHT_THRESHOLD) ||
        (hpt !== null && hpt < HIDDEN_HEIGHT_THRESHOLD)
      ) {
        hiddenRows.add(idx);
      }
    });
  }

  // Wipe cells in hidden rows BEFORE any further processing. Some
  // SheetJS versions compact / re-index rows inside `sheet_to_json`
  // in ways that make an index-based hidden-row filter unreliable — a
  // hidden row's data can land on what looks like a visible row's
  // index in the output array. Physically deleting the cells at their
  // raw addresses is the only way to guarantee the data never reaches
  // the AI regardless of how sheet_to_json numbers its output.
  if (hiddenRows.size > 0) {
    const ref = sheet['!ref'];
    const range = ref
      ? XLSX.utils.decode_range(ref)
      : { s: { r: 0, c: 0 }, e: { r: (sheet['!rows']?.length || 0) - 1, c: 100 } };
    hiddenRows.forEach((r: number) => {
      for (let c = range.s.c; c <= range.e.c; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        delete sheet[addr];
      }
    });
  }

  // Resolve merged cells — fill merged ranges with the top-left
  // cell's value. xlsx only stores the value in the top-left cell of
  // a merge; the rest are empty. Merges rooted in a hidden row are
  // skipped (source just got wiped above), and any destination cells
  // that fall on hidden rows are skipped too so we don't repopulate
  // what we just wiped.
  if (sheet['!merges']) {
    for (const merge of sheet['!merges']) {
      // merge.s = start {r, c}, merge.e = end {r, c}
      if (hiddenRows.has(merge.s.r)) continue;
      const startCell = sheet[XLSX.utils.encode_cell({ r: merge.s.r, c: merge.s.c })];
      const value = startCell ? (startCell.w || startCell.v || '') : '';
      for (let r = merge.s.r; r <= merge.e.r; r++) {
        if (hiddenRows.has(r)) continue;
        for (let c = merge.s.c; c <= merge.e.c; c++) {
          if (r === merge.s.r && c === merge.s.c) continue; // Skip the source cell
          const addr = XLSX.utils.encode_cell({ r, c });
          sheet[addr] = { t: 's', v: value, w: String(value) };
        }
      }
    }
  }

  // Convert to array of arrays (rows)
  const rows: any[][] = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: null,
    raw: false, // Get formatted strings for dates
  });

  // Filter out completely empty rows. Hidden rows are already wiped
  // above — after wiping they naturally fall out here as all-empty,
  // so no index-based filter is needed (and would be unreliable
  // anyway per the note on the wipe step above).
  const visibleNonEmptyRows: { row: any[]; originalIdx: number }[] = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (row.some(cell => cell !== null && cell !== undefined && cell !== '')) {
      visibleNonEmptyRows.push({ row, originalIdx: i + 1 });
    }
  }

  // Trim null/empty trailing cells from each row to reduce payload
  const trimmedRows = visibleNonEmptyRows.map(({ row, originalIdx }) => {
    // Find last non-null cell index
    let lastIdx = row.length - 1;
    while (lastIdx >= 0 && (row[lastIdx] === null || row[lastIdx] === undefined || row[lastIdx] === '')) {
      lastIdx--;
    }
    return { row: row.slice(0, lastIdx + 1), originalIdx };
  });

  // Remove rows that are likely disclaimers/boilerplate (long single-cell text at bottom)
  const filteredRows = trimmedRows.filter(({ row }) => {
    const firstCell = String(row[0] || '');
    // Skip rows where the first cell is a long sentence (>80 chars) and no other cells have data
    // These are typically disclaimers, indemnity text, and legal notices
    const otherCellsEmpty = row.slice(1).every((c: any) => !c || c === '');
    if (firstCell.length > 80 && otherCellsEmpty) return false;
    return true;
  });

  // Convert to compact format for AI — use tab-separated for smaller payload
  const formatted = filteredRows.map(({ row, originalIdx }) => {
    return `R${originalIdx}: ${JSON.stringify(row)}`;
  }).join('\n');

  return {
    data: formatted,
    hiddenRowCount: hiddenRows.size,
    totalRows: rows.length,
    visibleRows: visibleNonEmptyRows.length,
  };
};

/**
 * Extract data from MULTIPLE sheets and concatenate into a single payload
 * for the AI, with delimiters so the model can tell them apart. The prompt
 * has a corresponding rule to merge all sheets' station rows into one
 * stations array while taking the header from the first sheet only.
 *
 * Row counts are summed across sheets so the hidden-row warning stays
 * useful. If a single sheet is passed the output is identical to
 * `extractSheetData` with no delimiter overhead.
 */
export const extractMultipleSheets = (
  workbook: XLSX.WorkBook,
  sheetNames: string[],
): SheetExtractionResult => {
  if (sheetNames.length === 0) {
    throw new Error('extractMultipleSheets called with no sheet names');
  }
  if (sheetNames.length === 1) {
    return extractSheetData(workbook, sheetNames[0]);
  }

  const chunks: string[] = [];
  let hiddenRowCount = 0;
  let totalRows = 0;
  let visibleRows = 0;

  for (const name of sheetNames) {
    const chunk = extractSheetData(workbook, name);
    // Delimiter format is what the prompt looks for; keep in sync with
    // the "multiple sheets" rule in chaseListParsingPrompt.ts.
    chunks.push(`=== SHEET: "${name}" ===\n${chunk.data}`);
    hiddenRowCount += chunk.hiddenRowCount;
    totalRows += chunk.totalRows;
    visibleRows += chunk.visibleRows;
  }

  return {
    data: chunks.join('\n\n'),
    hiddenRowCount,
    totalRows,
    visibleRows,
  };
};

/**
 * Send raw spreadsheet data to Gemini for AI-assisted parsing
 */
export const parseChaseListWithAI = async (
  rawData: string,
  sheetName: string,
  broadcasters: OrgLookupEntry[] = [],
  mediaOwners: OrgLookupEntry[] = [],
  studios: OrgLookupEntry[] = [],
  agencies: OrgLookupEntry[] = [],
  clients: OrgLookupEntry[] = [],
  existingCampaign: ExistingCampaignForMerge | null = null,
): Promise<ParsedChaseList> => {
  // Get API key from chatbot config (shared Gemini key)
  const { config } = useChatbotStore.getState();

  if (!config || !config.apiKey) {
    throw new Error('Gemini API key not configured. Check chatbot settings.');
  }

  const prompt = createChaseListParsingPrompt(rawData, sheetName, broadcasters, mediaOwners, studios, agencies, clients, existingCampaign);

  // Always request the model's full output ceiling (gemini-2.5-flash hard-
  // caps at 65536). Sizing the budget off `rawData.length` UNDER-provisions
  // consolidated chase lists — their input is compact, but the output has to
  // reproduce every row's delivery address VERBATIM (200+ chars each), so the
  // real output exceeds the input-based estimate and the response truncates
  // with MAX_TOKENS. maxOutputTokens is only a CEILING: Google bills for the
  // tokens actually generated, not this number, and latency tracks actual
  // output too — so requesting the max is free on small parses and simply
  // removes the false truncation.
  const maxOutputTokens = 65536;

  const requestBody = {
    contents: [
      {
        role: 'user',
        parts: [{ text: prompt }],
      },
    ],
    generationConfig: {
      temperature: 0,
      topK: 1,
      topP: 0.1,
      maxOutputTokens,
      thinkingConfig: { thinkingBudget: 0 },
    },
  };

  // Retry with backoff for rate limit errors
  const MAX_RETRIES = 3;
  let response: Response | null = null;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    response = await fetch(
      `${API_CONFIG.GEMINI.ENDPOINT}?key=${config.apiKey}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      }
    );

    if (response.ok) break;

    // Check if rate limited (429 or quota error)
    if (response.status === 429 && attempt < MAX_RETRIES) {
      const waitSeconds = Math.min(15 * (attempt + 1), 45); // 15s, 30s, 45s
      console.warn(`[ChaseListParser] Rate limited, retrying in ${waitSeconds}s (attempt ${attempt + 1}/${MAX_RETRIES})`);
      await new Promise(resolve => setTimeout(resolve, waitSeconds * 1000));
      continue;
    }

    // Also catch quota errors in 200 responses with error bodies
    const errorData = await response.json().catch(() => ({}));
    const errorMsg = errorData.error?.message || 'Unknown error';
    if (errorMsg.includes('quota') && attempt < MAX_RETRIES) {
      const retryMatch = errorMsg.match(/retry in ([\d.]+)s/i);
      const waitSeconds = retryMatch ? Math.ceil(parseFloat(retryMatch[1])) + 2 : 15 * (attempt + 1);
      console.warn(`[ChaseListParser] Quota exceeded, retrying in ${waitSeconds}s (attempt ${attempt + 1}/${MAX_RETRIES})`);
      await new Promise(resolve => setTimeout(resolve, waitSeconds * 1000));
      continue;
    }

    throw new Error(`Gemini API error: ${errorMsg}`);
  }

  if (!response || !response.ok) {
    throw new Error('Gemini API error: max retries exceeded');
  }

  const data = await response.json();
  const finishReason = data.candidates?.[0]?.finishReason;
  if (finishReason === 'MAX_TOKENS') {
    console.warn('[ChaseListParser] Response was truncated due to token limit');
  }
  const textResponse = data.candidates?.[0]?.content?.parts?.[0]?.text;

  if (!textResponse) {
    throw new Error('Empty response from Gemini');
  }

  // Extract JSON from response — try multiple strategies
  let jsonString: string | null = null;

  // Strategy 1: Look for JSON inside markdown code blocks
  const codeBlockMatch = textResponse.match(/```(?:json)?\s*\n?([\s\S]*?)\n?```/);
  if (codeBlockMatch) {
    jsonString = codeBlockMatch[1].trim();
  }

  // Strategy 2: Find the outermost { ... } block
  if (!jsonString) {
    const firstBrace = textResponse.indexOf('{');
    const lastBrace = textResponse.lastIndexOf('}');
    if (firstBrace !== -1 && lastBrace > firstBrace) {
      jsonString = textResponse.substring(firstBrace, lastBrace + 1);
    }
  }

  if (!jsonString) {
    throw new Error('Could not extract JSON from Gemini response');
  }

  // Clean up common JSON issues from AI responses.
  // 1. Trailing commas before `}` or `]` — Gemini emits these occasionally.
  jsonString = jsonString.replace(/,\s*([\]}])/g, '$1');
  // 2. Missing commas between adjacent objects in an array (e.g. `}\n{`) —
  //    the most common failure on long stations arrays. Insert a comma so
  //    the parser recovers. Same for adjacent arrays. Only fires when the
  //    tokens sit on separate lines / whitespace to avoid touching legal
  //    JSON like `}{` inside a string (impossible for well-formed JSON
  //    outside of strings, but the strict regex keeps us safe).
  jsonString = jsonString.replace(/}(\s*\n\s*){/g, '},$1{');
  jsonString = jsonString.replace(/](\s*\n\s*)\[/g, '],$1[');

  try {
    const parsed: ParsedChaseList = JSON.parse(jsonString);

    // Validate basic structure
    if (!parsed.header || !Array.isArray(parsed.stations)) {
      throw new Error('Invalid response structure: missing header or stations');
    }

    return parsed;
  } catch (err: any) {
    // Surface the surrounding text at the failure position so we can tell
    // whether the response was truncated, contained an unescaped char, or
    // hit some other AI quirk. Without this the "position 42532" number is
    // useless to a human debugging in the field.
    const posMatch = /position (\d+)/.exec(err.message || '');
    let context = '';
    if (posMatch) {
      const pos = parseInt(posMatch[1], 10);
      const start = Math.max(0, pos - 120);
      const end = Math.min(jsonString.length, pos + 120);
      context = ` — near: "${jsonString.slice(start, end).replace(/\n/g, '\\n')}"`;
    }
    if (finishReason === 'MAX_TOKENS') {
      context += ` (response was truncated by MAX_TOKENS — chase list too large for output limit)`;
    }
    throw new Error(`Failed to parse Gemini response as JSON: ${err.message}${context}`);
  }
};

/**
 * Full pipeline: read file → extract sheet → parse with AI
 */
export const parseChaseListFile = async (
  file: File,
  sheetName: string,
  broadcasters: OrgLookupEntry[] = [],
  mediaOwners: OrgLookupEntry[] = [],
  studios: OrgLookupEntry[] = [],
  agencies: OrgLookupEntry[] = [],
  clients: OrgLookupEntry[] = [],
  existingCampaign: ExistingCampaignForMerge | null = null,
): Promise<ParsedChaseList> => {
  // Read the file
  const isCSV = file.name.toLowerCase().endsWith('.csv');
  const { workbook } = isCSV
    ? await readCSVFile(file)
    : await readExcelFile(file);

  // Extract raw data from the selected sheet
  const extraction = extractSheetData(workbook, sheetName);

  // Parse with AI
  const result = await parseChaseListWithAI(extraction.data, sheetName, broadcasters, mediaOwners, studios, agencies, clients, existingCampaign);

  return result;
};
